import React from 'react'
import { SimpleGrid } from '@chakra-ui/react'
import { Briefcase, User, Eye, GitBranch, CheckCircle } from 'lucide-react'
import StatCard from '../common/StatCard'

interface RecruitmentStatCardsProps {
  totalCandidates?: number
  openPipeline?: number
  totalReviewedCandidates?: number
  avgTimeToHire?: number
  totalHiredCandidates?: number
  isLoading: boolean
}

interface RecruitmentStatDef {
  key: string
  label: string
  icon: React.ReactNode
  color: string
  bg: string
  value: string | null
}

export default function RecruitmentStatCards({
  totalCandidates,
  openPipeline,
  totalReviewedCandidates,
  avgTimeToHire,
  totalHiredCandidates,
  isLoading,
}: RecruitmentStatCardsProps) {
  // StatCard uses `color` both as a Chakra token and inside a CSS template literal
  // for the background glow effect (e.g., `${color}14`), so raw hex values are required here.
  const stats: RecruitmentStatDef[] = [
    {
      key: 'total-candidates',
      label: 'Total Candidates',
      icon: <User size={22} />,
      color: '#5d5ecb', // primary.100
      bg: '#efeffa',    // primary.0
      value: totalCandidates != null ? totalCandidates.toString() : null,
    },
    {
      key: 'total-hired',
      label: 'Total Hired',
      icon: <CheckCircle size={22} />,
      color: '#FB61A2', // pink.100
      bg: '#FFEFF6',    // pink.10
      value: totalHiredCandidates != null ? totalHiredCandidates.toString() : null,
    },
    {
      key: 'reviewed',
      label: 'Total Reviewed Candidates',
      icon: <Eye size={22} />,
      color: '#33CFFF', // aqua.100
      bg: '#f0fbff',    // aqua.0
      value: totalReviewedCandidates != null ? totalReviewedCandidates.toString() : null,
    },
    {
      key: 'open-pipeline',
      label: 'Open Pipeline',
      icon: <GitBranch size={22} />,
      color: '#40C4AA', // success.100
      bg: '#effefa',    // success.0
      value: openPipeline != null ? openPipeline.toString() : null,
    },
    {
      key: 'time-to-hire',
      label: 'Avg. Time to Hire',
      icon: <Briefcase size={22} />,
      color: '#ffbe4c', // warning.100
      bg: '#FFF6E0',    // warning.0
      value: avgTimeToHire != null ? `${avgTimeToHire}d` : null,
    },
  ]

  return (
    <SimpleGrid columns={{ base: 2, lg: 5 }} spacing={{ base: 3, md: 3 }}>
      {stats.map((stat) => (
        <StatCard
          key={stat.key}
          icon={stat.icon}
          label={stat.label}
          value={!isLoading ? stat.value : null}
          color={stat.color}
          bg={stat.bg}
          isLoading={isLoading}
        />
      ))}
    </SimpleGrid>
  )
}